import React from "react";
import PropTypes from "prop-types";

/**
 * Универсальный badge компонент с интеграцией дизайн-системы
 */
export const Badge = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  ...props
}) => {
  // Базовые классы
  const baseClasses =
    "inline-flex items-center justify-center font-bold rounded-full transition-all duration-200 uppercase tracking-wider";

  // Варианты badge'ов
  const variants = {
    primary: ["bg-primary-500", "text-text-inverse"].join(" "),

    secondary: [
      "bg-bg-tertiary",
      "text-text-primary",
      "border border-border",
    ].join(" "),

    success: ["bg-success-500", "text-text-inverse"].join(" "),

    danger: ["bg-danger-500", "text-text-inverse"].join(" "),

    warning: ["bg-warning-500", "text-text-inverse"].join(" "),

    info: ["bg-primary-400", "text-text-inverse"].join(" "),

    gray: [
      "bg-bg-tertiary",
      "text-text-secondary",
      "border",
      "border-border",
    ].join(" "),

    outline: ["bg-transparent", "border", "border-current"].join(" "),

    ghost: ["bg-transparent", "text-text-secondary"].join(" "),
  };

  // Размеры
  const sizes = {
    xs: "px-2 py-0.5 text-xs",
    sm: "px-2.5 py-0.5 text-xs",
    md: "px-3 py-1 text-xs",
    lg: "px-4 py-1.5 text-sm",
    xl: "px-5 py-2 text-base",
  };

  // Объединяем все классы
  const combinedClasses = [
    baseClasses,
    variants[variant],
    sizes[size],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={combinedClasses} {...props}>
      {children}
    </span>
  );
};

/**
 * Специализированный badge для отображения статусов
 */
export const StatusBadge = ({
  status,
  className = "",
  children,
  label,
  ...props
}) => {
  const statusConfig = {
    // Заказы
    pending: { variant: "warning", text: "Ожидает" },
    confirmed: { variant: "info", text: "Подтвержден" },
    preparing: { variant: "primary", text: "Готовится" },
    ready: { variant: "success", text: "Готов" },
    delivered: { variant: "success", text: "Доставлен" },
    cancelled: { variant: "danger", text: "Отменен" },

    // Меню
    active: { variant: "primary", text: "Активно" },
    inactive: { variant: "gray", text: "Неактивно" },
    draft: { variant: "warning", text: "Черновик" },
    archived: { variant: "gray", text: "Архив" },
    published: { variant: "success", text: "Опубликован" },

    // Платежи
    paid: { variant: "success", text: "Оплачено" },
    unpaid: { variant: "danger", text: "Не оплачено" },
    partial: { variant: "warning", text: "Частично" },

    // Общие
    new: { variant: "primary", text: "Новый" },
    updated: { variant: "info", text: "Обновлен" },
    deleted: { variant: "danger", text: "Удален" },
    warning: { variant: "warning", text: "Внимание" },
    error: { variant: "danger", text: "Ошибка" },

    // Статусы пользователей
    online: { variant: "success", text: "Онлайн" },
    offline: { variant: "gray", text: "Оффлайн" },
    busy: { variant: "danger", text: "Занят" },

    // Приоритеты
    high: { variant: "danger", text: "Высокий" },
    medium: { variant: "warning", text: "Средний" },
    low: { variant: "info", text: "Низкий" },
  };

  const config = statusConfig[status] || { variant: "secondary", text: status };

  return (
    <Badge variant={config.variant} className={className} {...props}>
      {children || label || config.text}
    </Badge>
  );
};

/**
 * Badge для уведомлений с счетчиком
 */
export const NotificationBadge = ({
  count,
  maxCount = 99,
  variant = "danger",
  className = "",
  ...props
}) => {
  if (count <= 0) {
    return null;
  }

  const displayCount = count > maxCount ? `${maxCount}+` : count.toString();
  const size = displayCount.length > 2 ? "sm" : "xs";

  return (
    <Badge
      variant={variant}
      size={size}
      className={`min-w-[1.25rem] h-5 ${className}`}
      {...props}
    >
      {displayCount}
    </Badge>
  );
};

// PropTypes
Badge.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "gray",
    "outline",
    "ghost",
  ]),
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  className: PropTypes.string,
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  className: PropTypes.string,
};

NotificationBadge.propTypes = {
  count: PropTypes.number.isRequired,
  maxCount: PropTypes.number,
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "gray",
  ]),
  className: PropTypes.string,
};

export default Badge;
